<template>
  <div>
    <button
      v-bind:class="
        checkoutDisabled || $store.state.cart.isCommittingOrder
          ? 'disabled'
          : ''
      "
      class="btn place-order-btn btn-primary btn-full-width"
      @click="placeOrder"
      v-if="$store.state.cart.shippingGroups"
    >
      Place order
    </button>
    <b-modal
      v-model="interruptModal"
      ref="orderInterrupt"
      class="int-modal"
      title="Please confirm your Curbside store"
      hide-footer
      static
    >
      <modal-body class="int-body">
        <p class="mb-3 d-flex flex-column">
          Pickup at:
          <strong>{{ this.$store.state.reservation.store.name }}</strong>
          {{ this.$store.state.reservation.store.address1 }} <br />
          {{ this.storeFullAddress }}
        </p>
        <p class="mb-3 d-flex flex-column">
          Scheduled on:
          <strong>{{ this.reservationFullDate }}</strong>
          <strong>{{ reservationTimeRange }}</strong>
        </p>
      </modal-body>
      <modal-footer class="modal-footer">
        <button class="btn btn-secondary mr-3" @click="changeStore">
          Change Store
        </button>
        <button class="btn btn-primary" @click="placeOrderFromInterrupt">
          Place order
        </button>
      </modal-footer>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import { reservation as reservationMixin } from '../mixins/reservation';
import featureFlagEnabled from '../mixins/featureFlagEnabled';
import gtmClickTrackingMixins from "../mixins/gtmClickTracking";

export default {
  name: 'CheckoutPlaceOrderButton',
  mixins: [reservationMixin, featureFlagEnabled, gtmClickTrackingMixins],
  data() {
    return {
      currentStoreId: window.heb.data.user.currentStoreId,
      interruptModal: false,
    };
  },
  computed: {
    ...mapGetters({
      hasValidPayment: 'cart/hasValidPayment',
      hasCreditAdded: 'profile/hasCreditAdded',
      selectedEbtCard: 'cart/selectedEbtCard',
      ageVerificationRequired: 'cart/ageVerificationRequired',
      ageVerificationCompleted: 'cart/ageVerificationCompleted',
    }),
    newPaymentMethodsEnabled() {
      return this.featureFlagEnabled('HATCH_980_NEW_CHECKOUT_PAYMENTS_UI');
    },
    checkoutDisabled() {
      // Show the "Place order" button as disabled if parts of the checkout page
      // are still loading or if they somehow have an empty cart
      if (
        typeof this.$store === 'undefined' ||
        this.$store.state.cart.isFetchingCart ||
        this.$store.state.cart.commerceItemCount < 1 ||
        !this.$store.state.versionedContent.loaded
      ) {
        return true;
      } else {
        return false;
      }
    },
    checkoutValidation() {
      // First validate checks not controlled by the user
      if (this.checkoutDisabled) {
        return false;
      }

      // Next, check user-entered data
      // If any of it is invalid, we'll still show the "Place order" button as enabled
      // but just alert the user accordingly when they try to checkout

      // User must have all contact information filled out
      var curbsideShippingGroup = this.$store.state.cart.shippingGroups.find(
        function(group) {
          return group.shippingMethod === 'curbsideShippingGroup';
        }
      );

      if (typeof curbsideShippingGroup.address.phoneNumber === 'undefined') {
        console.info('Checkout: User needs a phone number');
        return {
          success: false,
          errorCode: 'NO_PHONE_NUMBER',
          errorMessage: 'Please add a phone number by selecting "Edit" under your contact information below.',
        };
      }

      if (typeof curbsideShippingGroup.address.lastName === 'undefined') {
        console.info('Checkout: User needs a last name');
        return {
          success: false,
          errorCode: 'NO_LAST_NAME',
          errorMessage: 'Please add a last name by selecting "Edit" under your contact information below.',
        };
      }

      // Check user has a valid timeslot
      if (this.isInvalidTimeslot) {
        console.info('Checkout: User does not have a timeslot');
        this.showModal('change-timeslot-modal');
        return {
          success: false,
          errorCode: 'INVALID_TIMESLOT',
          errorMessage: 'Please choose a time slot to proceed to checkout.',
        };
      }

      // In the case of the old payment methods, an activeCardAlias is needed
      // In the new feature-flagged component, a selected payment of CC or EBT is acceptable
      // There should already be an error in the new payment methods, triggering the conditional before this, this is just a backup
      const hasInsufficientPayment = (!this.newPaymentMethodsEnabled && this.$store.state.profile.activeCardAlias === '')
        || (this.newPaymentMethodsEnabled && !this.hasValidPayment);

      // If the order is not pay at pickup, it must have a payment method on it.
      if (hasInsufficientPayment && !this.$store.state.cart.payAtPickupOrder) {
        console.info('Checkout: User does not have a sufficient payment method');
        return {
          success: false,
          errorCode: 'NO_CREDIT_CARD',
          errorMessage: `${this.hasCreditAdded ? 'Select' : 'Add'} a credit or debit card to place order.`,
          type: this.newPaymentMethodsEnabled ? 'paymentMethods' : 'general'
        };
      }

      // if (this.$store.state.profile.activeCardAlias === undefined && this.$store.state.cart.priceInfo.total.amount > 0) {
      //   console.info('Checkout: User does not have an activeCardAlias')
      //   return false
      // }

      return { success: true };
    },
    /** @return {object} the complete reservation object */
    reservation() {
      return this.$store.state.reservation || {};
    },
    /** @return {string} reservation time range for display */
    reservationTimeRange() {
      const { startTime, endTime } = this.reservation;

      return startTime && endTime
        ? 'between ' +
            moment.parseZone(startTime).format('h:mm A') +
            ' and ' +
            moment.parseZone(endTime).format('h:mm A')
        : '';
    },
    storeFullAddress() {
      return `${this.$store.state.reservation.store.city}, ${this.$store.state.reservation.store.state} ${this.$store.state.reservation.store.postalCode}`;
    },
    reservationFullDate() {
      return moment
        .parseZone(this.$store.state.reservation.date)
        .format('MMMM D, YYYY');
    },
    isInvalidTimeslot() {
      return (
        !this.$store.state.reservation.timeslotId ||
        this.$store.state.reservation.expiration.isPassed
      );
    }
  },
  methods: {
    changeStore: function() {
      this.interruptModal = false;
      dataLayer.push({ event: 'e_vic3Interrupt_storeChanged' });
      this.showModal('change-timeslot-modal');
    },
    placeOrder: function() {
      if (this.checkoutValidation.success !== true) {
        var error = {
          type: this.checkoutValidation.type || 'general',
          icon: 'fa-exclamation-triangle', // Accepts fontawesome 4 icon names.
          message: this.checkoutValidation.errorMessage, // text or html
          id: this.checkoutValidation.errorCode,
          code: '000',
          variant: 'danger', // warn, info, danger
        };
        this.$store.commit('errors/CLEAR_ERRORS');
        this.$store.commit('errors/PUSH_ERROR', error);
        if (!this.newPaymentMethodsEnabled) {
          window.scrollTo(0, 0);
        }
        return;
      }

      if(this.ageVerificationRequired && !this.ageVerificationCompleted) {
        this.$bvModal.show('ageVerificationRequired');
        return;
      }

      var body = {
        creditCardSource: 'SAVED',
        ageVerified: false,
        taxExempt: false,
        paymentAlias: !this.newPaymentMethodsEnabled ? this.$store.state.profile.activeCardAlias : '',
        tosToken: this.$store.state.versionedContent.tosToken,
        primaryPaymentMethod: this.$store.state.cart.payAtPickupOrder ? 'payAtPickup' : 'creditCard'
      };

      if (
        this.currentStoreId === '92' &&
        this.$store.state.reservation.fulfillmentType !== 'delivery'
      ) {
        this.interruptModal = true;
        dataLayer.push({ event: 'e_vic3Interrupt_shown' });
        return;
      }

      if (this.newPaymentMethodsEnabled && !!this.selectedEbtCard && window.__EXORDX__) {
        this.openEbtPinPadModal();
      } else {
        this.$store.dispatch('cart/POST_ORDER', body);
      }
    },
    placeOrderFromInterrupt: function() {
      dataLayer.push({ event: 'e_vic3Interrupt_orderPlaced' });
      if (this.checkoutValidation.success !== true) {
        var error = {
          type: 'general',
          icon: 'fa-exclamation-triangle', // Accepts fontawesome 4 icon names.
          message: this.checkoutValidation.errorMessage, // text or html
          id: this.checkoutValidation.errorCode,
          code: '000',
          variant: 'danger', // warn, info, danger
        };
        this.$store.commit('errors/CLEAR_ERRORS');
        this.$store.commit('errors/PUSH_ERROR', error);
      } else {
        var body = {
          creditCardSource: 'SAVED',
          ageVerified: false,
          taxExempt: false,
          paymentAlias: !this.newPaymentMethodsEnabled ? this.$store.state.profile.activeCardAlias : '',
          tosToken: this.$store.state.versionedContent.tosToken,
          primaryPaymentMethod: this.$store.state.cart.payAtPickupOrder ? 'payAtPickup' : 'creditCard'
        };

        if (this.newPaymentMethodsEnabled && !!this.selectedEbtCard && window.__EXORDX__) {
          this.openEbtPinPadModal();
        } else {
          this.$store.dispatch('cart/POST_ORDER', body);
        }
      }
      this.$refs.orderInterrupt.hide();
      if (!this.newPaymentMethodsEnabled) {
        window.scrollTo(0, 0);
      }
    },
    openEbtPinPadModal() {
      this.gtmLaunchEbtPinPad('checkout_commit', 'snapDebit');
      window.__EXORDX__.dispatch({
        type: "@@HEB/MODALS/SHOW_MODAL",
        payload: {
          modal: "EBT_PIN_PAD_MODAL",
          props: {
            paymentAlias: this.selectedEbtCard.paymentAlias,
            transactionType: 'snapDebit',
            // SNAP debit amount - only used for displaying to the user
            amount:  get(this.selectedEbtCard, "amount.formattedAmount", ""),
            onSuccess: this.postEbtOrder,
          }
        }
      });
    },
    postEbtOrder() {
      this.$store.dispatch('cart/POST_ORDER', {
        tosToken: this.$store.state.versionedContent.tosToken,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.btn-secondary {
  border-color: #dc2a29;
  color: #dc2a29;
  &:hover,
  &:focus,
  &:visited {
    border-color: #dc2a29;
    background-color: #f5f5f5;
    color: #dc2a29;
  }
}
.modal-header {
  padding-bottom: 0;
  button {
    font-size: 40px;
    padding-bottom: 0;
  }
}
.int-modal .modal-dialog {
  max-width: 600px;
}
.int-body {
  padding-top: 0;
  font-size: 14px;
  line-height: 1.5;
  h5 {
    margin-bottom: 1em;
  }
}
</style>
